/**
 * AIAgentService.js
 * Service to parse user commands and map them to dashboard actions
 */

// Define command patterns and their corresponding actions
const commandPatterns = {
  campaign: {
    patterns: ['create campaign', 'new campaign', 'add campaign', 'make campaign'],
    action: 'CREATE_CAMPAIGN',
    description: 'Create Campaign',
    navigation: '/dashboard/campaign_schemes',
    modal: 'addCampaign'
  },
  scheme: {
    patterns: ['create scheme', 'new scheme', 'add scheme', 'make scheme'],
    action: 'CREATE_SCHEME',
    description: 'Create Scheme',
    navigation: '/dashboard/campaign_schemes',
    modal: 'addScheme'
  },
  users: {
    patterns: ['view users', 'organization users', 'team members', 'all users'],
    action: 'VIEW_USERS',
    description: 'View Users',
    navigation: '/dashboard/organizationusers',
    modal: null
  },
  transactions: {
    patterns: ['view transactions', 'show transactions', 'transactions'],
    action: 'VIEW_TRANSACTIONS',
    description: 'View Transactions',
    navigation: '/dashboard/soundboxT',
    modal: null
  },
  merchants: {
    patterns: ['view merchants', 'list merchants', 'all merchants', 'merchants'],
    action: 'VIEW_MERCHANTS',
    description: 'View Merchants',
    navigation: '/dashboard/merchants',
    modal: null
  },
  merchant: {
    patterns: ['add merchant', 'new merchant', 'onboard merchant', 'create merchant'],
    action: 'ONBOARD_MERCHANT',
    description: 'Onboard Merchant',
    navigation: '/dashboard/merchants',
    modal: null
  },
  device: {
    patterns: ['onboard device', 'add device', 'new device', 'register device'],
    action: 'ONBOARD_DEVICE',
    description: 'Onboard Device',
    navigation: '/dashboard/deviceOnboarding',
    modal: null
  },
  user: {
    patterns: ['add user', 'new user', 'create user', 'invite user'],
    action: 'ADD_USER',
    description: 'Add User',
    navigation: '/dashboard/organizationusers',
    modal: null
  },
  ticket: {
    patterns: ['create ticket', 'new ticket', 'report issue', 'raise ticket'],
    action: 'CREATE_TICKET',
    description: 'Create Ticket',
    navigation: '/dashboard/ticketManagement',
    modal: null
  }
};

const commandPatternMaker = {
  merchant: {
    patterns: ['add merchant', 'new merchant', 'onboard merchant', 'create merchant'],
    action: 'ONBOARD_MERCHANT',
    description: 'Onboard Merchant',
    navigation: '/dashboard/merchants',
    modal: null
  },
  merchants: {
    patterns: ['view merchants', 'list merchants', 'all merchants', 'merchants'],
    action: 'VIEW_MERCHANTS',
    description: 'View Merchants',
    navigation: '/dashboard/merchants',
    modal: null
  },
  transactions: {
    patterns: ['view transactions', 'show transactions', 'transactions'],
    action: 'VIEW_TRANSACTIONS',
    description: 'View Transactions',
    navigation: '/dashboard/chanakya',
    modal: null
  }
};

/**
 * Parse user input and find matching command
 * @param {string} userInput - User's natural language input
 * @returns {object|null} - Matched command object or null
 */
export const parseCommand = (userInput, role) => {
  if (!userInput || userInput.trim().length === 0) {
    return null;
  }

  const input = userInput.toLowerCase().trim();

  const pattern = role === ('maker' || role === 'checker') ? commandPatternMaker : commandPatterns;

  // Search through all command patterns (avoid for..of for lint rules)
  const entries = Object.entries(pattern);
  for (let i = 0; i < entries.length; i += 1) {
    const key = entries[i][0];
    const command = entries[i][1];
    const patterns = command.patterns || [];
    for (let j = 0; j < patterns.length; j += 1) {
      const pattern = patterns[j];
      if (input.includes(pattern)) {
        return {
          ...command,
          commandKey: key,
          matchedPattern: pattern,
          confidence: calculateConfidence(input, pattern)
        };
      }
    }
  }

  return null;
};

/**
 * Calculate confidence score based on how well the input matches the pattern
 * @param {string} input - User input
 * @param {string} pattern - Matched pattern
 * @returns {number} - Confidence score (0-1)
 */
const calculateConfidence = (input, pattern) => {
  const matchLength = pattern.length;
  const inputLength = input.length;

  // Perfect match gets 100% confidence
  if (input === pattern) {
    return 1;
  }

  // Exact word match gets high confidence
  if (input.split(' ').includes(pattern.split(' ')[0])) {
    return 0.9;
  }

  // Partial match gets medium confidence
  return 0.7;
};

/**
 * Get all available commands for help/suggestions
 * @returns {array} - Array of available commands
 */
export const getAvailableCommands = (role) => {
  const pattern = role === 'checker' || role === 'maker' ? commandPatternMaker : commandPatterns;

  return Object.entries(pattern).map(([key, command]) => ({
    key,
    action: command.action,
    description: command.description,
    examplePatterns: command.patterns.slice(0, 2)
  }));
};

/**
 * Execute AI command
 * @param {string} userInput - User's input
 * @param {function} navigate - React Router navigate function
 * @param {function} onModalOpen - Callback to open modal
 * @returns {object} - Result of command execution
 */
export const executeAICommand = async (role, userInput, navigate, onModalOpen) => {
  const command = parseCommand(userInput, role);

  if (!command) {
    return {
      success: false,
      message:
        "Sorry, I didn't understand that command. Try 'Create Campaign', 'Add Merchant', or 'View Transactions'.",
      command: null
    };
  }

  try {
    // Navigate to the required page
    if (command.navigation) {
      navigate(command.navigation);

      // Wait a bit for navigation to complete, then open modal if needed
      if (command.modal && onModalOpen) {
        setTimeout(() => {
          onModalOpen(command.modal);
        }, 500);
      }
    }

    return {
      success: true,
      message: `Opening ${command.description}...`,
      command: command.action,
      action: command
    };
  } catch (error) {
    return {
      success: false,
      message: 'Error executing command. Please try again.',
      command: null,
      error
    };
  }
};

/**
 * Dummy AI suggestions (Predictions / Insights) for Chanakya pages.
 * Non-disruptive helpers to return small, page-aware mock data when user
 * clicks on "Prediction" or "Insight" in the UI.
 */
const suggestionMap = {
  merchants: {
    predictions: [
      {
        title: 'High-Value Merchants at Risk',
        summary:
          'Top 15% revenue-generating merchants show declining weekly activity (≥20% drop). Without intervention, expect ₹2-4L revenue impact within 30 days.',
        confidence: 'high'
      },
      {
        title: 'New Merchant Activation Window',
        summary:
          'Merchants onboarded in last 14 days are 3x more likely to adopt if re-engaged now. Post-onboarding campaigns will unlock 25-35% additional transaction volume.',
        confidence: 'high'
      },
      {
        title: 'Device Failure Cascade Risk',
        summary:
          '12% of active merchants face device connectivity issues. Expected downtime: 4-8 hours. Proactive device support needed to prevent churn.',
        confidence: 'medium'
      }
    ],

    insights: [
      {
        title: '⚠️ Revenue Leak: Inactive High-Value Merchants',
        summary:
          '3 merchants show zero transactions in past 7 days. Likely causes: device malfunction, settlement delays, or unresolved support tickets. This shouldnot have happened considering festival season.',
        recommendedAction:
          'Immediate outreach + device health check for these 8 merchants within 24 hours'
      },
      {
        title: 'Campaign ROI Gap: Untapped Segments',
        summary:
          '₹8L+ in potential monthly GMV is locked away—merchants transacting without active schemes/incentives. Current campaign reach: only 35% of active base.',
        recommendedAction:
          'Launch retargeting campaign to 65% merchants; expected ROI: 4.2x in 45 days'
      }
    ]
  },
  transactions: {
    predictions: [
      {
        title: 'Transaction Peak Window Identified',
        summary:
          'Friday-Sunday 6-10 PM shows 2.3x higher transaction volume. Expected peak GMV: ₹6.5L this weekend. Allocate customer support and device inventory accordingly.',
        confidence: 'high'
      },
      {
        title: 'Campaign Lift Multiplier Opportunity',
        summary:
          'Merchants exposed to campaigns show 1.8x higher transaction frequency. Activating 60 dormant campaigns will drive ₹12-15L additional GMV within 2 weeks.',
        confidence: 'high'
      },
      {
        title: 'Transaction Failure Rate Trending Up',
        summary:
          'Failed transactions increased 35% week-over-week (now 2.1%). Root cause: network latency during peak hours. Expected revenue impact if not fixed: ₹2.8L/month.',
        confidence: 'medium'
      }
    ],

    insights: [
      {
        title: '💡 Quick Win: Unannounced Transactions',
        summary:
          '₹4.2L in monthly transactions happen without merchant awareness of active schemes. Merchants are leaving money on the table—low-hanging fruit for incremental revenue.',
        recommendedAction:
          'Create smart announcements for high-value unannounced merchants; projected uplift: 22-28% per merchant'
      },
      {
        title: 'Operational Bottleneck: Peak Hour Failures',
        summary:
          'Transaction success rate drops from 98.5% (off-peak) to 94.2% (peak hours 7-9 PM). 156 failed transactions = ₹94K lost last week.',
        recommendedAction:
          'Increase API capacity and add failover queue; expected cost: ₹8K/month vs. ₹94K revenue impact'
      },
      {
        title: 'Device Health Crisis: Silent Revenue Drain',
        summary:
          '23 merchants with "online" devices but zero transactions in 48 hours. Likely offline despite status flag. Masks real system health and causes merchant frustration.',
        recommendedAction: 'Implement real transaction-based device health check within 6 hours'
      }
    ]
  },
  campaign_schemes: {
    predictions: [
      {
        title: 'Campaign Performance Gap: Same-Day Activation Needed',
        summary:
          '42 prepared campaigns sit dormant. Historical data: campaigns activated within 24h of creation show 34% higher engagement. Activate now to capture holiday season demand.',
        confidence: 'high'
      },
      {
        title: 'Scheme Complexity Killer: Simplification ROI',
        summary:
          'Multi-condition schemes see 2.8x lower adoption vs. simple incentive schemes. 5 complex schemes underperforming by ₹3.1L. Simplify terms → expect 65% uplift.',
        confidence: 'high'
      },
      {
        title: 'Regional Campaign Concentration Risk',
        summary:
          'North region merchants are oversaturated (4 active campaigns). South region has zero active campaigns despite 2.2x merchant growth this quarter. Rebalance to capture untapped ₹5.8L potential.',
        confidence: 'medium'
      }
    ],

    insights: [
      {
        title: '🎯 Critical: Campaign Overlap Killing Performance',
        summary:
          '3 campaigns targeting same 180 merchants within 48-hour window. Message fatigue confirmed—engagement dropped 44%. Each campaign individually solid; together = waste.',
        recommendedAction:
          'Stagger campaigns by 72 hours; consolidate into 1 mega-campaign if possible; recover ₹2.1L in GMV lift'
      },
      {
        title: 'Device Health Skewing Campaign Metrics',
        summary:
          'Campaigns show 18% lower delivery rate to "online" merchants who go offline mid-delivery. 31% of message failures are device-timing issues, not merchant disinterest.',
        recommendedAction:
          'Implement pre-campaign device health filter; retarget failed merchants 24h later; improve effective ROI by 22%'
      },
      {
        title: 'Scheme Messaging Misalignment Costing Sales',
        summary:
          'Merchants using schemes show 58% lower conversion than those just hearing about promotions. Evidence suggests benefit messaging is unclear or incentive is perceived as too small.',
        recommendedAction:
          'A/B test clearer messaging + 5% higher incentive tier; pilot with 12 high-value merchants for 10 days'
      }
    ]
  },
  vendors: {
    predictions: [
      {
        title: 'Vendor Churn Risk: Contract Renewal Window',
        summary:
          '7 vendors have renewal milestones within 30 days. Historical: 28% churn at renewal without proactive engagement. Expected contract value at risk: ₹4.2L. Engage now.',
        confidence: 'high'
      },
      {
        title: 'Device Pool Underutilization Costing Growth',
        summary:
          'Vendor A has 200 devices allocated, only 67 active. Vendor D has 45 devices, 43 active. Gap = opportunity for 40+ additional active merchants. Rebalance to unlock ₹1.8L quarterly revenue.',
        confidence: 'high'
      },
      {
        title: 'Payment Default Risk Spike',
        summary:
          '3 vendors are 8-12 days overdue on payments (vs. 0-2 day average). Payment delays correlate with service degradation. Early intervention can prevent account suspension and ₹280K revenue loss.',
        confidence: 'medium'
      }
    ],

    insights: [
      {
        title: '🚨 Single Point of Failure: Contact Dependency',
        summary:
          '12 vendors rely on one primary contact. Last month: 4 contacts unavailable; resulting delays averaged 3.2 days. Vendor communication is bottlenecked and fragile.',
        recommendedAction:
          'Enforce secondary contact registration this week; set up auto-escalation to avoid future delays'
      },
      {
        title: 'Device Health Variance: Quality Risk',
        summary:
          'Vendor B devices show 7.8% failure rate vs. Vendor C at 1.2%. Performance differential directly impacts merchant experience and retention. Vendor B support intervention needed.',
        recommendedAction:
          'Audit Vendor B device fleet this week; implement replacement/repair SLA; expected reliability gain: 5.2 percentage points'
      },
      {
        title: 'SLA Breach Trend: Escalating Support Costs',
        summary:
          'Vendor support ticket resolution time increased 34% (avg 4.2 days). 2 vendors consistently miss SLAs. Driving repeat tickets and merchant frustration.',
        recommendedAction:
          'Performance review + penalties review for 2 vendors; implement response-time tracking dashboard immediately'
      }
    ]
  },
  default: {
    predictions: [
      {
        title: 'Revenue Uplift Opportunity: Top 40% Merchants',
        summary:
          'Top 40% of merchants by GMV are only engaged via 30% of platforms/regions. Cross-selling untapped channels → projected ₹8-12L incremental monthly revenue with zero new onboarding cost.',
        confidence: 'high'
      },
      {
        title: 'Holiday Season Capacity Crunch Warning',
        summary:
          'System is tracking 2.4x typical transaction spike for Dec 20-31. Current infrastructure supports 85% of projected peak. Expect bottlenecks without load balancing NOW. Impact: ₹2-3L lost GMV.',
        confidence: 'high'
      },
      {
        title: 'Merchant Reactivation Gold Mine',
        summary:
          '180 merchants went inactive in last 60 days (were transacting previously). Win-back cost: 1/8th of new merchant acquisition. 30% historical reactivation rate = ₹2.8L potential GMV recovery.',
        confidence: 'high'
      },
      {
        title: 'Device Replacement Cycle ROI',
        summary:
          '78 devices are 18+ months old (planned retirement age). Replacement today = 0% failure rate vs. current 4.2%. Prevents service disruptions; ROI breaks even in 6 weeks via revenue protection.',
        confidence: 'medium'
      },
      {
        title: 'Year-End Planning Imperative',
        summary:
          'Dec-Jan typically drives 38% surge in merchant signups. Campaign pipeline empty; onboarding team at 70% capacity. Critical window closing in 96 hours to prepare infrastructure and campaigns.',
        confidence: 'high'
      },

      {
        title: '⚠️ Crisis: Campaign Pipeline Emptiness',
        summary:
          'Zero campaigns scheduled for Dec 24-31 (highest-demand period). 52 merchants actively monitoring for promotions. Letting holiday demand evaporate = ₹3.8L avoidable loss.',
        recommendedAction:
          'Launch 4 holiday campaigns by EOD tomorrow; use proven high-performers; estimated incremental GMV: ₹3.5-4.2L'
      },
      {
        title: '📊 Data Gap',
        summary:
          'Cannot distinguish between "recently onboarded but performing well" vs. "recently onboarded but stuck." 34% of new merchants never reach 2nd transaction. Root cause unknown; fixing visibility is prerequisite to intervention.',
        recommendedAction:
          'Implement post-onboarding health dashboard this week; identify blockers within 48 hours'
      },
      {
        title: 'Untapped Revenue: Device Downtime Patterns',
        summary:
          'Devices go offline predictably (Mon 12-2 AM = 23% offline rate). Scheduled maintenance invisible to merchants. Yet system forces 90% of offline penalties onto merchants. Perception = unreliability.',
        recommendedAction:
          'Publish maintenance windows; negotiate SLA maintenance windows with vendors.'
      },
      {
        title: 'Support System Misalignment Driving Churn',
        summary:
          "Merchants with >1 support ticket show 40% lower 30-day retention. Yet support response time averages 2.8 days. Issue doesn't resolve itself; merchant gives up and leaves.",
        recommendedAction:
          'Implement same-day support SLA for first 2 tickets; expected churn reduction: 12-15%'
      },
      {
        title: 'Year-End Preparation Checklist',
        summary:
          'Year-end scenario planning complete. System capacity confirmed for projected 2.4x peak. Campaigns ready for launch. But 2 critical dependencies remain: vendor readiness (80%), payment gateway stress-test (pending).',
        recommendedAction: 'Complete payment gateway testing + vendor readiness calls.'
      }
    ]
  },
  individual_merchant: {
    predictions: [
      {
        title: 'Transaction Volume Risk: Short-Term Drop Detected',
        summary:
          'Merchant transaction volume over the last 3 days is below their recent baseline. Button usage trend shows more volume decrease than increase, indicating reduced customer engagement. If this persists, expected revenue impact could be seen within the next 7 days.',
        confidence: 'medium'
      },
      {
        title: 'Device Reliability Risk: Battery & Heartbeat Pattern',
        summary:
          'Device battery is trending downward with intermittent heartbeat gaps observed in the last week. Similar patterns historically precede device downtime or missed announcements, impacting transaction success.',
        confidence: 'high'
      },
      {
        title: 'Support Load Probability: Ticket History Signal',
        summary:
          'Merchant has a non-zero count of open or pending support tickets. Merchants with unresolved tickets show higher chances of repeat issues and transaction hesitation within the same billing cycle.',
        confidence: 'medium'
      },
      {
        title: '🔌 Device Health Attention Needed',
        summary:
          'Battery percentage and reboot trend indicate growing device stress. Proactive maintenance can prevent sudden offline events during peak hours.',
        recommendedAction:
          'Schedule a preventive device check or battery replacement; monitor heartbeat trend daily for the next 72 hours'
      },
      {
        title: 'Button Usage',
        summary:
          'Volume increase actions outweigh decreases, indicating rising customer interaction and improved merchant confidence in using the device.',
        recommendedAction:
          'Reinforce momentum by acknowledging positive usage trends and ensure device volume is optimised for peak hours'
      },
      {
        title: '🚚 Delivery & Onboarding Closure Signal',
        summary:
          'Device delivery lifecycle shows multiple state transitions before final delivery. Delays or reversals during dispatch increase merchant onboarding friction.',
        recommendedAction:
          'Confirm successful device handover and perform a post-delivery onboarding call to ensure merchant confidence'
      }
    ]
  },
  riskManagement: {
    predictions: [
      {
        title: 'Transaction Volume Risk: Abnormal Spike Window Identified',
        summary:
          'Merchant transaction volume shows a concentrated spike during late-evening windows (9:00 PM – 10:30 PM) compared to their historical baseline. Such short-duration surges, when repeated across days, increase operational and reputational risk if left unmonitored.',
        confidence: 'high'
      },
      {
        title: 'Time-of-Day Risk: Recurring High-Risk Activity Periods',
        summary:
          'AI identifies consistent transaction clustering during off-peak hours (8:30 PM – 10:00 PM) relative to the merchant’s usual operating pattern. Similar time-based deviations often correlate with elevated anomaly probability over the next billing cycle.',
        confidence: 'medium'
      },
      {
        title: 'Campaign Exposure Risk: Pattern Recurrence Likely',
        summary:
          'Active or recent campaigns are driving transaction concentration at specific price points, primarily during campaign-driven surge windows (7:00 PM – 9:00 PM). Historical signals suggest a high likelihood of similar volume and amount patterns recurring during upcoming campaign windows.',
        confidence: 'high'
      },
      {
        title: 'Behavioral Stability Risk: Week-on-Week Variance Increase',
        summary:
          'Merchant transaction behavior shows increasing variance across weekly intervals, with repeated deviations observed during late-evening hours (post 8:00 PM). Sustained divergence from baseline patterns may indicate heightened monitoring requirements in the near term.',
        confidence: 'medium'
      }
    ],

    insights: [
      {
        title: '📊 Elevated Monitoring Recommended During Peak Windows',
        summary:
          'Late-evening transaction windows (9:00 PM – 10:30 PM) show repeated volume concentration compared to merchant norms. These windows represent periods of elevated behavioral risk.',
        recommendedAction:
          'Apply heightened monitoring during 9:00 PM – 10:30 PM across the next 7 days'
      },
      {
        title: '🧭 Merchant Behavior Shifting from Historical Baseline',
        summary:
          'Recent transaction patterns show gradual drift from established merchant behavior, particularly during evening peak periods (8:00 PM – 9:30 PM).',
        recommendedAction:
          'Continue baseline recalibration and review week-on-week variance for evening time slots'
      },
      {
        title: '🎯 Campaign Influence on Risk Profile',
        summary:
          'Campaign activity is materially influencing transaction timing and amount concentration during campaign surge windows (7:00 PM – 9:00 PM), increasing short-term behavioral risk exposure.',
        recommendedAction:
          'Align campaign monitoring with 7:00 PM – 9:00 PM windows and review incentive thresholds'
      }
    ]
  },

  fraudDetection: {
    predictions: [
      {
        title: 'Amount-Based Anomaly Risk: Repetition Likely to Persist',
        summary:
          'Identical-amount transaction clustering observed in recent periods is likely to continue during upcoming peak windows (8:30 PM – 10:00 PM), based on historical recurrence patterns.',
        confidence: 'high'
      },
      {
        title: 'Time-Window Misuse Risk: High-Probability Recurrence',
        summary:
          'Transactions concentrated in narrow off-peak windows (9:00 PM – 10:15 PM) show a strong likelihood of repetition over the next few days, increasing anomaly detection probability.',
        confidence: 'medium'
      },
      {
        title: 'Campaign-Driven Misuse Risk: Elevated During Active Periods',
        summary:
          'Current campaign mechanics are expected to continue driving artificial transaction patterns during active campaign windows (7:00 PM – 9:00 PM).',
        confidence: 'high'
      }
    ],

    insights: [
      {
        title: '💰 Repetitive Amount Pattern Detected',
        summary:
          'A high proportion of transactions are clustered around identical amounts within short time intervals, primarily during peak evening slots (8:45 PM – 9:45 PM). This behavior deviates from the merchant’s normal sales mix and may indicate artificial transaction structuring.',
        recommendedAction:
          'Monitor identical-amount transactions during 8:45 PM – 9:45 PM and review campaign thresholds influencing payment behavior'
      },
      {
        title: '⏰ Time-Based Transaction Anomaly',
        summary:
          'Transaction activity is concentrated in narrow time windows outside the merchant’s usual peak hours, notably between 9:00 PM – 10:15 PM. This recurring pattern elevates anomaly likelihood when combined with volume surges.',
        recommendedAction: 'Increase monitoring during 9:00 PM – 10:15 PM across upcoming days'
      },
      {
        title: '📊 Short-Duration Volume Clustering',
        summary:
          'AI detected rapid bursts of transactions occurring within compressed timeframes (10–20 minute bursts between 9:10 PM – 9:40 PM). Such clustering exceeds normal hourly distribution patterns observed for this merchant.',
        recommendedAction:
          'Track repeat burst activity between 9:10 PM – 9:40 PM and correlate with campaign or external triggers'
      },
      {
        title: '🎯 Campaign-Linked Behavioral Signal',
        summary:
          'Transaction patterns show strong alignment with campaign-specific price points and launch timings, especially during campaign surge periods (7:00 PM – 9:00 PM). Similar campaign-linked behavior has previously correlated with elevated misuse indicators.',
        recommendedAction:
          'Re-evaluate campaign mechanics and closely monitor 7:00 PM – 9:00 PM transaction distributions'
      }
    ]
  }
};

const normalizePageKey = (page) => {
  if (!page) return 'default';
  const p = page.toString().toLowerCase();

  if (p.includes('vendorlot') || p.includes('vendor-lot')) return 'vendor_lots';
  if (p.includes('vendor')) return 'vendors';
  if (p.includes('merchant')) return 'merchants';
  if (p.includes('transaction') || p.includes('soundboxt')) return 'transactions';
  if (p.includes('campaign') || p.includes('scheme')) return 'campaign_schemes';
  if (p.includes('soundboxdevicedetails')) return 'individual_merchant';

  return 'default';
};

/**
 * Returns a Promise that resolves to a dummy prediction or insight for a page.
 * @param {string} page - Page identifier or path (e.g., '/dashboard/merchants')
 * @param {'prediction'|'insight'} type - Type of suggestion
 * @returns {Promise<object>} - Mock suggestion object
 */
const pickRandom = (arr = []) => {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return arr[Math.floor(Math.random() * arr.length)];
};

export const fetchDummyAIAgentSuggestion = (page, type) => {
  const key = normalizePageKey(page);
  let entry;
  if (type === 'riskManagement') {
    entry = suggestionMap[type];
  } else if (type === 'fraudDetection') {
    entry = suggestionMap[type];
  } else {
    entry = suggestionMap[type] || suggestionMap.default;
  }
  return new Promise((resolve) => {
    setTimeout(() => {
      let bucket;

      // If no type passed, randomise
      if (!type || type === 'riskManagement' || type === 'fraudDetection') {
        bucket = Math.random() > 0.5 ? 'predictions' : 'insights';
      } else {
        bucket = type === 'prediction' ? 'predictions' : 'insights';
      }

      const suggestion = pickRandom(entry[bucket]);

      resolve({
        page: key,
        type: bucket === 'predictions' ? 'prediction' : 'insight',
        suggestion
      });
    }, 2000);
  });
};

// Convenience synchronous getters (non-blocking) for tests or quick UI updates
export const getDummyPrediction = (page) => {
  const key = normalizePageKey(page);
  const entry = suggestionMap[key] || suggestionMap.default;
  const suggestion = pickRandom(entry.predictions) || null;
  return { page: key, suggestion };
};

export const getDummyInsight = (page) => {
  const key = normalizePageKey(page);
  const entry = suggestionMap[key] || suggestionMap.default;
  const suggestion = pickRandom(entry.insights) || null;
  return { page: key, suggestion };
};
